import { createContext, useContext, useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Loader2,
} from "lucide-react";
import { site } from "@/config/site";
import { leadFormOptions } from "@/data/content";
import { trackEvent, getAttribution } from "@/lib/tracking";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const LeadFormContext = createContext(null);
export const useLeadForm = () => useContext(LeadFormContext);

const postcodeOk = (v) =>
  /^[A-Za-z]{1,2}\d[A-Za-z\d]?\s*\d[A-Za-z]{2}$/.test(v.trim());
const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
const phoneOk = (v) => v.replace(/[^\d]/g, "").length >= 10;

const initial = {
  interest: "",
  rooms: [],
  address1: "",
  address2: "",
  town: "",
  postcode: "",
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  preferredTime: "",
  message: "",
  consentService: false,
  consentMarketing: false,
};

const stepTitles = [
  "What are you interested in?",
  "Which rooms are you transforming?",
  "Where are we visiting?",
  "Your contact details",
  "Final touches",
];

const inputCls =
  "w-full rounded-xl border border-warm-300 bg-white px-4 py-3.5 text-base focus:border-navy-900 focus:outline-none";

export function LeadFormProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [data, setData] = useState(initial);
  const [meta, setMeta] = useState({ source: "website", productInterest: null });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  const openForm = (opts = {}) => {
    setMeta({
      source: opts.source || "website",
      productInterest: opts.productInterest || null,
    });
    if (opts.productInterest) {
      setData((d) => ({ ...d, interest: opts.productInterest }));
    }
    setOpen(true);
    setStep(0);
    setError("");
    trackEvent("book_visit_click", { source: opts.source || "website" });
    trackEvent("quote_form_start", { source: opts.source || "website" });
  };

  const closeForm = () => setOpen(false);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const toggleRoom = (r) =>
    setData((d) => ({
      ...d,
      rooms: d.rooms.includes(r)
        ? d.rooms.filter((x) => x !== r)
        : [...d.rooms, r],
    }));

  const canNext = useMemo(() => {
    if (step === 0) return !!data.interest;
    if (step === 1) return data.rooms.length > 0;
    if (step === 2) return !!data.address1.trim() && postcodeOk(data.postcode);
    if (step === 3)
      return (
        data.firstName.trim() &&
        data.lastName.trim() &&
        phoneOk(data.phone) &&
        emailOk(data.email)
      );
    return data.consentService;
  }, [step, data]);

  const next = () => {
    if (!canNext) return;
    trackEvent("quote_form_step_complete", { step: step + 1 });
    setStep((s) => Math.min(s + 1, 4));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    if (!canNext || submitting) return;
    setSubmitting(true);
    setError("");
    const attr = getAttribution();
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/leads`,
        {
          ...data,
          source: meta.source,
          productInterest: meta.productInterest,
          page: location.pathname,
          utm: attr.utm || {},
          gclid: attr.gclid || "",
          referrer: attr.referrer || "",
          landing_page: attr.landing_page || "",
        },
        { headers: { "Content-Type": "application/json" } },
      );
      trackEvent("quote_form_submit", { interest: data.interest });
      trackEvent("lead_form_submit", { interest: data.interest, source: meta.source });
      trackEvent("home_visit_booking", { interest: data.interest, source: meta.source });
      setOpen(false);
      navigate("/thank-you");
    } catch (e) {
      setError(
        "Something went wrong sending your enquiry. Please try again, or call us on " +
          site.phone.display +
          ".",
      );
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setStep(0);
        setData(initial);
        setError("");
      }, 300);
    }
  }, [open]);

  return (
    <LeadFormContext.Provider value={{ openForm, closeForm }}>
      {children}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-lg w-[calc(100vw-2rem)] rounded-2xl border-warm-200 p-0 overflow-hidden bg-warm-50"
          data-testid="lead-form-modal"
        >
          <DialogTitle className="sr-only">
            Book a Free Design Visit
          </DialogTitle>
          <DialogDescription className="sr-only">
            A short five-step enquiry form to request your free home design
            visit.
          </DialogDescription>
            <div className="p-6 sm:p-8">
              <div className="flex items-center justify-between mb-6">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-navy-800">
                  Free Design Visit
                </p>
                <p className="text-xs text-slate-500">
                  Step {step + 1} of 5
                </p>
              </div>
              <div
                className="h-1 rounded-full bg-warm-200 mb-7 overflow-hidden"
                role="progressbar"
                aria-valuenow={step + 1}
                aria-valuemin={1}
                aria-valuemax={5}
              >
                <motion.div
                  className="h-full bg-navy-900 rounded-full"
                  animate={{ width: `${((step + 1) / 5) * 100}%` }}
                  transition={{ duration: 0.4 }}
                />
              </div>
              <h2 className="font-serif text-2xl text-slate-900 mb-6">
                {stepTitles[step]}
              </h2>
              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -24 }}
                  transition={{ duration: 0.3 }}
                >
                  {step === 0 && (
                    <div className="grid grid-cols-2 gap-3" role="radiogroup" aria-label="Product interest">
                      {leadFormOptions.interests.map((i) => (
                        <button
                          key={i}
                          type="button"
                          data-testid={`lead-interest-${i.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                          onClick={() => set("interest", i)}
                          aria-pressed={data.interest === i}
                          className={`rounded-xl border px-4 py-4 text-sm font-medium text-left transition-all duration-200 ${
                            data.interest === i
                              ? "border-navy-900 bg-navy-900 text-white shadow-lg shadow-navy-900/20"
                              : "border-warm-300 bg-white hover:border-navy-800/50"
                          }`}
                        >
                          {i}
                        </button>
                      ))}
                    </div>
                  )}
                  {step === 1 && (
                    <div className="flex flex-wrap gap-2" aria-label="Rooms">
                      {leadFormOptions.rooms.map((r) => (
                        <button
                          key={r}
                          type="button"
                          data-testid={`lead-room-${r.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                          onClick={() => toggleRoom(r)}
                          aria-pressed={data.rooms.includes(r)}
                          className={`rounded-full border px-4 py-2.5 text-sm transition-all duration-200 ${
                            data.rooms.includes(r)
                              ? "border-navy-900 bg-navy-900 text-white"
                              : "border-warm-300 bg-white hover:border-navy-800/50"
                          }`}
                        >
                          {r}
                        </button>
                      ))}
                    </div>
                  )}
                  {step === 2 && (
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="lead-address1" className="text-sm font-medium text-slate-700 block mb-2">
                          Address
                        </label>
                        <input
                          id="lead-address1"
                          data-testid="lead-address1-input"
                          value={data.address1}
                          onChange={(e) => set("address1", e.target.value)}
                          placeholder="House number and street"
                          autoComplete="address-line1"
                          className={inputCls}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label htmlFor="lead-address2" className="text-sm font-medium text-slate-700 block mb-2">
                            Address line 2 <span className="text-slate-400 font-normal">(optional)</span>
                          </label>
                          <input
                            id="lead-address2"
                            data-testid="lead-address2-input"
                            value={data.address2}
                            onChange={(e) => set("address2", e.target.value)}
                            autoComplete="address-line2"
                            className={inputCls}
                          />
                        </div>
                        <div>
                          <label htmlFor="lead-town" className="text-sm font-medium text-slate-700 block mb-2">
                            Town / City
                          </label>
                          <input
                            id="lead-town"
                            data-testid="lead-town-input"
                            value={data.town}
                            onChange={(e) => set("town", e.target.value)}
                            autoComplete="address-level2"
                            placeholder="Birmingham"
                            className={inputCls}
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="lead-postcode" className="text-sm font-medium text-slate-700 block mb-2">
                          Postcode
                        </label>
                        <input
                          id="lead-postcode"
                          data-testid="lead-postcode-input"
                          value={data.postcode}
                          onChange={(e) => set("postcode", e.target.value)}
                          placeholder="e.g. B10 0NR"
                          autoComplete="postal-code"
                          className={`${inputCls} uppercase`}
                        />
                        <p className="text-xs text-slate-500 mt-3">
                          We cover Birmingham and the surrounding area — your
                          address helps us plan your visit and confirm coverage.
                        </p>
                      </div>
                    </div>
                  )}
                  {step === 3 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {[
                        ["firstName", "First name", "given-name", "text"],
                        ["lastName", "Last name", "family-name", "text"],
                        ["phone", "Mobile number", "tel", "tel"],
                        ["email", "Email address", "email", "email"],
                      ].map(([k, label, auto, type]) => (
                        <div key={k}>
                          <label htmlFor={`lead-${k}`} className="text-sm font-medium text-slate-700 block mb-2">
                            {label}
                          </label>
                          <input
                            id={`lead-${k}`}
                            data-testid={`lead-${k}-input`}
                            type={type}
                            value={data[k]}
                            autoComplete={auto}
                            onChange={(e) => set(k, e.target.value)}
                            className={inputCls}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  {step === 4 && (
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="lead-time" className="text-sm font-medium text-slate-700 block mb-2">
                          Preferred appointment day/time (optional)
                        </label>
                        <select
                          id="lead-time"
                          data-testid="lead-time-select"
                          value={data.preferredTime}
                          onChange={(e) => set("preferredTime", e.target.value)}
                          className={inputCls}
                        >
                          <option value="">No preference</option>
                          <option>Weekday morning</option>
                          <option>Weekday afternoon</option>
                          <option>Weekday evening</option>
                          <option>Saturday (by appointment)</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="lead-message" className="text-sm font-medium text-slate-700 block mb-2">
                          Anything else we should know? (optional)
                        </label>
                        <textarea
                          id="lead-message"
                          data-testid="lead-message-input"
                          rows={3}
                          value={data.message}
                          onChange={(e) => set("message", e.target.value)}
                          className={`${inputCls} resize-none`}
                        />
                      </div>
                      <label className="flex items-start gap-3 text-sm text-slate-600 cursor-pointer">
                        <input
                          type="checkbox"
                          data-testid="lead-consent-service"
                          checked={data.consentService}
                          onChange={(e) => set("consentService", e.target.checked)}
                          className="mt-1 h-4 w-4 accent-[#0B1D3A]"
                        />
                        <span>
                          I'm happy for My Blinds Company to contact me about my
                          enquiry, as described in the{" "}
                          <a href="/privacy-policy" className="underline text-navy-800">
                            Privacy Policy
                          </a>
                          . *
                        </span>
                      </label>
                      <label className="flex items-start gap-3 text-sm text-slate-600 cursor-pointer">
                        <input
                          type="checkbox"
                          data-testid="lead-consent-marketing"
                          checked={data.consentMarketing}
                          onChange={(e) => set("consentMarketing", e.target.checked)}
                          className="mt-1 h-4 w-4 accent-[#0B1D3A]"
                        />
                        <span>
                          I'd also like to receive occasional offers and news
                          (optional — you can opt out at any time).
                        </span>
                      </label>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
              {error && (
                <p role="alert" data-testid="lead-form-error" className="text-sm text-red-700 mt-4">
                  {error}
                </p>
              )}
              <div className="flex items-center justify-between mt-8">
                {step > 0 ? (
                  <button
                    type="button"
                    data-testid="lead-form-back"
                    onClick={back}
                    className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-navy-900 transition-colors"
                  >
                    <ArrowLeft className="h-4 w-4" /> Back
                  </button>
                ) : (
                  <span />
                )}
                {step < 4 ? (
                  <button
                    type="button"
                    data-testid="lead-form-next"
                    onClick={next}
                    disabled={!canNext}
                    className="inline-flex items-center gap-2 rounded-full bg-navy-900 px-6 py-3 text-sm font-medium text-white shadow-lg shadow-navy-900/10 transition-all duration-300 hover:bg-navy-800 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Continue <ArrowRight className="h-4 w-4" />
                  </button>
                ) : (
                  <button
                    type="button"
                    data-testid="lead-form-submit"
                    onClick={submit}
                    disabled={!canNext || submitting}
                    className="inline-flex items-center gap-2 rounded-full bg-navy-900 px-6 py-3 text-sm font-medium text-white shadow-lg shadow-navy-900/10 transition-all duration-300 hover:bg-navy-800 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                    Request My Free Design Visit
                  </button>
                )}
              </div>
            </div>
        </DialogContent>
      </Dialog>
    </LeadFormContext.Provider>
  );
}

